import { c as _c } from "react/compiler-runtime";
import { useFormatter, useTranslations } from 'next-intl';
import { Progress } from '@/components/ui/app-progress';
import { Icon } from '@/components/ui/icon';
import { ArrowDataTransferHorizontalIconSolid, LimitationIconSolid } from '@/components/ui/icon/glyphs';
import { useMarketOutcomeColor } from '@/hooks/chart/use-outcome-color';
import { outcomeHueToVar } from '@/lib/chart/outcome-hue';
import { useFormattedCurrency } from '@/lib/currency';
import type { OutcomeRowVM } from './derive';
import { formatResolvedPct } from './other-outcomes-format';
import { OutcomeThumb } from './other-outcomes-parts';
interface OtherOutcomeCardProps {
  row: OutcomeRowVM;
  rank: number;
  marketPubkey: string;
}
const METRIC_DIVIDER = '•';
const MAX_CONVICTION_PERCENT = 200;
export function OtherOutcomeCard(t0) {
  const $ = _c(70);
  const {
    row,
    rank,
    marketPubkey
  } = t0;
  const t = useTranslations("market.resolvedPanel");
  const formatter = useFormatter();
  const {
    formatD6
  } = useFormattedCurrency();
  const colors = useMarketOutcomeColor(marketPubkey, row.index);
  let t1;
  if ($[0] !== row.finalSharePct) {
    t1 = formatResolvedPct(row.finalSharePct);
    $[0] = row.finalSharePct;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const finalShare = t1;
  const t2 = row.isWinner ? "true" : undefined;
  let t3;
  if ($[2] !== formatter || $[3] !== rank || $[4] !== row.isWinner) {
    t3 = row.isWinner ? undefined : formatter.number(rank);
    $[2] = formatter;
    $[3] = rank;
    $[4] = row.isWinner;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== marketPubkey || $[7] !== row.imageNsfw || $[8] !== row.imageUrl || $[9] !== row.index || $[10] !== row.isWinner || $[11] !== t3) {
    t4 = <OutcomeThumb marketPubkey={marketPubkey} index={row.index} imageUrl={row.imageUrl} imageNsfw={row.imageNsfw} size="size-12" showSettledBadge={row.isWinner} rankLabel={t3} />;
    $[6] = marketPubkey;
    $[7] = row.imageNsfw;
    $[8] = row.imageUrl;
    $[9] = row.index;
    $[10] = row.isWinner;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== row.label) {
    t5 = <span className="break-words text-body font-medium text-foreground-primary">{row.label}</span>;
    $[13] = row.label;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t) {
    t6 = t("tableVolume");
    $[15] = t;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t6) {
    t7 = <span className="sr-only">{t6}</span>;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Icon icon={ArrowDataTransferHorizontalIconSolid} aria-hidden={true} className="size-3.5 shrink-0" />;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const t9 = row.volumeTotalD6 ?? "0";
  let t10;
  if ($[20] !== formatD6 || $[21] !== t9) {
    t10 = formatD6(t9, "compact");
    $[20] = formatD6;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10) {
    t11 = <span>{t10}</span>;
    $[23] = t10;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t11 || $[26] !== t7) {
    t12 = <span className="inline-flex items-center gap-1">{t7}{t8}{t11}</span>;
    $[25] = t11;
    $[26] = t7;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <span aria-hidden={true} className="mx-0.5 text-foreground-tertiary">{METRIC_DIVIDER}</span>;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t) {
    t14 = t("tablePeak");
    $[29] = t;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t14) {
    t15 = <span className="sr-only">{t14}</span>;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Icon icon={LimitationIconSolid} aria-hidden={true} className="size-3.5 shrink-0" />;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== row.peakConsensusPct) {
    t17 = formatResolvedPct(row.peakConsensusPct);
    $[34] = row.peakConsensusPct;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t17) {
    t18 = <span>{t17}</span>;
    $[36] = t17;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== t15 || $[39] !== t18) {
    t19 = <span className="inline-flex items-center gap-1">{t15}{t16}{t18}</span>;
    $[38] = t15;
    $[39] = t18;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== t12 || $[42] !== t19) {
    t20 = <div className="flex flex-wrap items-center gap-1 text-caption tabular-nums text-foreground-secondary">{t12}{t13}{t19}</div>;
    $[41] = t12;
    $[42] = t19;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== t20 || $[45] !== t5) {
    t21 = <div className="flex min-w-0 flex-1 flex-col gap-1.5">{t5}{t20}</div>;
    $[44] = t20;
    $[45] = t5;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== t21 || $[48] !== t4) {
    t22 = <div className="flex min-w-0 items-center gap-3">{t4}{t21}</div>;
    $[47] = t21;
    $[48] = t4;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  const t23 = Math.min(MAX_CONVICTION_PERCENT, Math.max(0, row.finalSharePct));
  const t24 = row.label;
  let t25;
  if ($[50] !== t) {
    t25 = t("tableFinal");
    $[50] = t;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  const t26 = `${t24}: ${t25}`;
  let t27;
  if ($[52] !== colors.hue) {
    t27 = colors.hue === undefined ? "var(--color-foreground-secondary)" : outcomeHueToVar(colors.hue, "text");
    $[52] = colors.hue;
    $[53] = t27;
  } else {
    t27 = $[53];
  }
  let t28;
  if ($[54] !== t27) {
    t28 = {
      backgroundColor: t27
    };
    $[54] = t27;
    $[55] = t28;
  } else {
    t28 = $[55];
  }
  let t29;
  if ($[56] !== finalShare || $[57] !== t23 || $[58] !== t26 || $[59] !== t28) {
    t29 = <Progress value={t23} max={MAX_CONVICTION_PERCENT} aria-label={t26} aria-valuetext={finalShare} className="h-1.5 min-w-0 flex-1 bg-background-tertiary" indicatorStyle={t28} />;
    $[56] = finalShare;
    $[57] = t23;
    $[58] = t26;
    $[59] = t28;
    $[60] = t29;
  } else {
    t29 = $[60];
  }
  let t30;
  if ($[61] !== finalShare) {
    t30 = <span className="shrink-0 text-caption tabular-nums text-foreground-secondary">{finalShare}</span>;
    $[61] = finalShare;
    $[62] = t30;
  } else {
    t30 = $[62];
  }
  let t31;
  if ($[63] !== t29 || $[64] !== t30) {
    t31 = <div className="flex min-w-0 items-center gap-3">{t29}{t30}</div>;
    $[63] = t29;
    $[64] = t30;
    $[65] = t31;
  } else {
    t31 = $[65];
  }
  let t32;
  if ($[66] !== t2 || $[67] !== t22 || $[68] !== t31) {
    t32 = <li data-testid="resolved-panel-outcome-row" data-winner={t2} className="flex min-w-0 flex-col gap-3">{t22}{t31}</li>;
    $[66] = t2;
    $[67] = t22;
    $[68] = t31;
    $[69] = t32;
  } else {
    t32 = $[69];
  }
  return t32;
}